import * as React from 'react';
import { FilterAnchor, signed, TextSkeleton, type CardProps, type FilterLink } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import { Skeleton } from '@chess/api-skeleton';
import type { MiddlegamePosition } from '@chess/api-model';
import { MIN_POSITION_GAMES, type MiddlegamePositionsResult, type PositionRow } from './MiddlegamePositionsAggregator.ts';
import { POSITIONS_HELP } from './help.tsx';
import './middlegame-positions.css';

// "Positions you misplay" (2026-10-10; the Middlegame screen's `middlegame.why`):
// the reader's average score change in pawns per middlegame in each kind of
// position, a bar each around zero, red where ground was lost, green where
// it was gained, worst first, the worst five of the kinds in five or more
// middlegames (the aggregator's `MAX_POSITION_ROWS`, `MIN_POSITION_GAMES`;
// the note says when some are not listed); a bar and its label open the
// library at `mg-position=<kind>`, the games counted. Drawn from props only:
// the card's body, which the section draws in the dashboard's frame
// (`MIDDLEGAME_POSITIONS_CARD`, `registerCard`).

export type MiddlegamePositionsCardProps = CardProps<{ result: MiddlegamePositionsResult }, {
  /** Opens the library filtered to a row's games. */
  link?: FilterLink;
}>;

/** The frame's title and help topic: the registration's and the stories'. */
export const MIDDLEGAME_POSITIONS_CARD = { title: 'Positions you misplay', help: POSITIONS_HELP };
const ASIDE = 'score change by position type';

export const POSITION_LABEL: Record<MiddlegamePosition, string> = {
  'queens-on': 'Queens on',
  'queens-off': 'Queens off',
  'open-centre': 'Open centre',
  'closed-centre': 'Closed centre',
  'opposite-castling': 'Opposite castling',
  'exposed-king': 'Your king exposed',
  'bishop-pair': 'Your bishop pair',
  'their-bishop-pair': 'Their bishop pair',
  'exchange-up': 'Exchange up',
  'exchange-down': 'Exchange down',
};

const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;
const types = (n: number) => `${n} ${n === 1 ? 'type' : 'types'}`;

/** The card's one-line note: the measure, the middlegames, and which kinds are listed when not all are. */
export function positionsNote(r: Pick<MiddlegamePositionsResult, 'middlegames' | 'rows' | 'ranked' | 'hidden'>): string {
  const head = `pawns per middlegame · ${r.middlegames} middlegames`;
  if (!r.hidden && r.rows.length === r.ranked) return `${head} · games at the right`;
  const shown = r.rows.length === r.ranked ? types(r.ranked) : `worst ${r.rows.length} of ${types(r.ranked)}`;
  return `${head} · ${shown} with ${MIN_POSITION_GAMES}+ games`;
}

/** The rows: a label, a bar from the centre line scaled to the largest change, the average and the games. */
export const PositionBars: React.FC<{ rows: PositionRow[]; link?: FilterLink }> = ({ rows, link }) => {
  const max = Math.max(0.5, ...rows.map((r) => Math.abs(r.average)));
  return (
    <div className="mg-positions" role="list">
      {rows.map((r) => {
        const filter = { middlegamePosition: r.kind };
        const title = `${POSITION_LABEL[r.kind]}: ${signed(r.average)} pawns per middlegame · ${games(r.games)} · opens them`;
        const width = `${(50 * Math.abs(r.average)) / max}%`;
        const lost = r.average < 0;
        return (
          <div className="mg-positions-row" role="listitem" key={r.kind} title={title}>
            <FilterAnchor className="mg-positions-label" link={link} filter={filter}>
              {POSITION_LABEL[r.kind]}
            </FilterAnchor>
            <FilterAnchor className="mg-positions-track" link={link} filter={filter}>
              <span
                className="mg-positions-fill"
                style={{ width, ...(lost ? { right: '50%' } : { left: '50%' }), background: lost ? 'var(--viz-score-loss)' : 'var(--viz-score-gain)' }}
              />
            </FilterAnchor>
            <span className="mg-positions-value">{signed(r.average)}</span>
            <span className="mg-positions-games">{r.games}</span>
          </div>
        );
      })}
    </div>
  );
};

const SAMPLE: PositionRow[] = [
  { kind: 'exposed-king', games: 6, average: -1.4 },
  { kind: 'closed-centre', games: 9, average: -0.8 },
  { kind: 'queens-on', games: 20, average: -0.3 },
  { kind: 'open-centre', games: 11, average: 0.2 },
  { kind: 'queens-off', games: 7, average: 0.6 },
];

export const MiddlegamePositionsCard: React.FC<MiddlegamePositionsCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <Skeleton loading shape={<PositionBars rows={SAMPLE} />} label="Loading the position types" />
        <CardNote>
          <TextSkeleton sample="pawns per middlegame · 24 middlegames · worst 5 of 8 types with 5+ games" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  if (!result.rows.length)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>
          {!result.middlegames
            ? 'No scanned middlegames in the last 30 days.'
            : result.hidden
              ? `No position type came up in ${MIN_POSITION_GAMES} or more of your middlegames yet.`
              : 'No position type lasted 3 of your moves in a middlegame.'}
        </CardEmpty>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <PositionBars rows={result.rows} link={link} />
      <CardNote>{positionsNote(result)}</CardNote>
    </>
  );
};
